import type { ManiaLane } from '@shared/maniaNote';
import { withAlpha, lightenToward } from '../../piano/render/pianoRenderColour';
import {
  DANGER_WINDOW_FRAC,
  HOLD_CHEVRON_CYCLE_S,
  LANE_GEM_ACCENTS,
  LANE_GEM_COLOURS,
} from './guitarRenderConstants';
import {
  computeLayout,
  perspectiveProgress,
  type GuitarLayout,
} from '../guitarPlayfieldGeometry';
import type { GuitarRenderInput } from './guitarRenderTypes';
import type { RenderQuality } from '../../rendering/renderQuality';
import {
  computeDangerPulse,
  drawGemAt,
  drawHoldChevronStream,
  highwayProgressY,
  laneCentreXAtProgress,
  laneWidthAtProgress,
} from './guitarNoteHelpers';

/**
 * Draws the gems + hold ribbons sliding down the perspective
 * highway. Mirrors the Piano tile renderer's responsibilities (cull
 * out-of-window events, dispatch each visible event to the right
 * shape primitive, anchor visuals to the engine's geometric perfect
 * window) but the math projects every coordinate through the
 * trapezoid: gems near the back are smaller and dimmer, gems at the
 * strum bar are foreground-sized and saturated.
 *
 * Owns a `firstVisibleIdx` cursor so a long chart doesn't iterate
 * the whole event array every frame — the same per-frame win the
 * Piano TileRenderer captures.
 */
export class GuitarNoteRenderer {
  private firstVisibleIdx = 0;
  /** Phase 0..1 driving the flowing-chevron animation on engaged hold ribbons. */
  private chevronPhase = 0;

  reset(): void {
    this.firstVisibleIdx = 0;
    this.chevronPhase = 0;
  }

  advance(dt: number): void {
    if (dt <= 0 || HOLD_CHEVRON_CYCLE_S <= 0) {
      return;
    }
    this.chevronPhase = (this.chevronPhase + dt / HOLD_CHEVRON_CYCLE_S) % 1;
  }

  draw(
    ctx: CanvasRenderingContext2D,
    input: GuitarRenderInput,
    layout: GuitarLayout,
    reducedMotion: boolean,
    quality: RenderQuality,
  ): void {
    const lightweightGems = reducedMotion || !quality.tileShadows;
    const { chart, beat, state, laneEvents, perfectOverlapBeats, autoMissCutoffBeats } =
      input;
    const dangerWindowBeats = perfectOverlapBeats * DANGER_WINDOW_FRAC;
    const beatsPerMs = chart.bpm / 60_000;
    const approachBeats = chart.approachMs * beatsPerMs;
    const upperBound = beat + approachBeats * 1.05;
    const lowerBound = beat - autoMissCutoffBeats * 1.05;

    while (this.firstVisibleIdx < laneEvents.length) {
      const ev = laneEvents[this.firstVisibleIdx];
      const isHold = ev.holdEndBeat !== undefined;
      const visibleBeat = isHold ? Math.max(ev.beat, ev.holdEndBeat!) : ev.beat;
      if (visibleBeat < lowerBound) {
        this.firstVisibleIdx += 1;
      } else {
        break;
      }
    }

    for (let i = this.firstVisibleIdx; i < laneEvents.length; i += 1) {
      const ev = laneEvents[i];
      if (state.resolvedReleaseIds.has(ev.id)) {
        continue;
      }
      const isHold = ev.holdEndBeat !== undefined;
      const headDone = state.resolvedIds.has(ev.id);
      if (headDone && (!isHold || !state.heldLanes.has(ev.lane))) {
        continue;
      }
      const visibleBeat = isHold ? Math.max(ev.beat, ev.holdEndBeat!) : ev.beat;
      if (visibleBeat < lowerBound) {
        continue;
      }
      if (ev.beat > upperBound) {
        break;
      }
      const isDual = ev.kind === 'dual';

      if (isHold) {
        this.drawHold(
          ctx,
          ev,
          beat,
          approachBeats,
          layout,
          state.heldLanes.has(ev.lane),
          headDone,
          isDual,
          dangerWindowBeats,
          perfectOverlapBeats,
          lightweightGems,
          reducedMotion,
        );
        continue;
      }

      this.drawGem(
        ctx,
        ev.lane,
        ev.beat,
        beat,
        approachBeats,
        layout,
        isDual,
        dangerWindowBeats,
        perfectOverlapBeats,
        lightweightGems,
        reducedMotion,
      );
    }
  }

  /**
   * Single tap gem at lane `lane` due at `gemBeat`. Position the
   * gem's centre at the y corresponding to its progress along the
   * highway, scale + tint by depth so receding gems read as smaller
   * and dimmer. The shared {@link drawGemAt} helper handles the
   * trail + approach pulse on top of the basic perspective placement.
   */
  private drawGem(
    ctx: CanvasRenderingContext2D,
    lane: ManiaLane,
    gemBeat: number,
    currentBeat: number,
    approachBeats: number,
    layout: GuitarLayout,
    isDual: boolean,
    dangerWindowBeats: number,
    perfectOverlapBeats: number,
    lightweightGems: boolean,
    reducedMotion: boolean,
  ): void {
    const progress = 1 - (gemBeat - currentBeat) / approachBeats;
    if (progress < 0) {
      return;
    }
    const dangerPulse = computeDangerPulse(
      currentBeat - gemBeat,
      perfectOverlapBeats,
      dangerWindowBeats,
    );
    drawGemAt(ctx, layout, lane, progress, isDual, dangerPulse, lightweightGems, reducedMotion);
  }

  /**
   * Hold ribbon for a hold note. Drawn as a tapered quadrilateral
   * (perspective-correct: top is the tail, bottom is the head)
   * connecting `holdEndBeat` (top, far) → `beat` (bottom, near).
   * Engaged holds (player has tapped the head and is currently
   * holding) brighten + grow a chevron stream so the player has
   * "you're scoring" feedback throughout the sustain.
   */
  private drawHold(
    ctx: CanvasRenderingContext2D,
    ev: { id: string; lane: ManiaLane; beat: number; holdEndBeat?: number; kind?: string },
    currentBeat: number,
    approachBeats: number,
    layout: GuitarLayout,
    heldActive: boolean,
    headDone: boolean,
    isDual: boolean,
    dangerWindowBeats: number,
    perfectOverlapBeats: number,
    lightweightGems: boolean,
    reducedMotion: boolean,
  ): void {
    const baseColour = LANE_GEM_COLOURS[ev.lane];
    const accentColour = LANE_GEM_ACCENTS[ev.lane];
    const tailBeat = ev.holdEndBeat!;
    const rawHeadProgress = 1 - (ev.beat - currentBeat) / approachBeats;
    const headProgress = headDone ? 1 : Math.max(0, Math.min(1, rawHeadProgress));
    const tailProgress = Math.max(
      0,
      Math.min(1, 1 - (tailBeat - currentBeat) / approachBeats),
    );

    // Both ends of the ribbon project through the same perspective
    // curve as standalone gems, so the sustain visually compresses
    // at the back and stretches toward the strum bar like the rest
    // of the highway — matches the gem trail rather than fighting it.
    const headScreenProgress = perspectiveProgress(headProgress);
    const tailScreenProgress = perspectiveProgress(tailProgress);
    const headY = highwayProgressY(layout, headScreenProgress);
    const tailY = highwayProgressY(layout, tailScreenProgress);
    const headLaneWidth = laneWidthAtProgress(layout, headScreenProgress);
    const tailLaneWidth = laneWidthAtProgress(layout, tailScreenProgress);
    const headCentreX = laneCentreXAtProgress(layout, ev.lane, headScreenProgress);
    const tailCentreX = laneCentreXAtProgress(layout, ev.lane, tailScreenProgress);

    const ribbonWidthFrac = 0.62;
    const headRibbonW = headLaneWidth * ribbonWidthFrac;
    const tailRibbonW = tailLaneWidth * ribbonWidthFrac;
    const dangerPulse = headDone
      ? 0
      : computeDangerPulse(
          currentBeat - ev.beat,
          perfectOverlapBeats,
          dangerWindowBeats,
        );

    const ribbonColour = heldActive
      ? lightenToward(baseColour, 0.4)
      : lightenToward(baseColour, 0.12);

    const shadowed = !lightweightGems && !reducedMotion;
    ctx.save();
    ctx.fillStyle = withAlpha(ribbonColour, heldActive ? 0.85 : 0.7);
    if (shadowed) {
      ctx.shadowColor = accentColour;
      ctx.shadowBlur = heldActive ? 18 : 10;
    }
    ctx.beginPath();
    ctx.moveTo(tailCentreX - tailRibbonW / 2, tailY);
    ctx.lineTo(tailCentreX + tailRibbonW / 2, tailY);
    ctx.lineTo(headCentreX + headRibbonW / 2, headY);
    ctx.lineTo(headCentreX - headRibbonW / 2, headY);
    ctx.closePath();
    ctx.fill();
    if (shadowed) {
      ctx.shadowBlur = 0;
    }

    if (heldActive) {
      ctx.fillStyle = withAlpha('#ffffff', 0.45);
      const stripeFrac = 0.16;
      ctx.beginPath();
      ctx.moveTo(tailCentreX - tailRibbonW * stripeFrac, tailY);
      ctx.lineTo(tailCentreX + tailRibbonW * stripeFrac, tailY);
      ctx.lineTo(headCentreX + headRibbonW * stripeFrac, headY);
      ctx.lineTo(headCentreX - headRibbonW * stripeFrac, headY);
      ctx.closePath();
      ctx.fill();
      if (!reducedMotion) {
        const chevronCount = lightweightGems ? 3 : undefined;
        drawHoldChevronStream(
          ctx,
          this.chevronPhase,
          tailY,
          headY,
          tailCentreX,
          headCentreX,
          tailRibbonW,
          headRibbonW,
          chevronCount,
        );
      }
    }

    if (dangerPulse > 0) {
      ctx.fillStyle = withAlpha('#ff3b5c', 0.35 + dangerPulse * 0.35);
      ctx.beginPath();
      ctx.moveTo(tailCentreX - tailRibbonW / 2, tailY);
      ctx.lineTo(tailCentreX + tailRibbonW / 2, tailY);
      ctx.lineTo(headCentreX + headRibbonW / 2, headY);
      ctx.lineTo(headCentreX - headRibbonW / 2, headY);
      ctx.closePath();
      ctx.fill();
    }
    ctx.restore();

    if (!headDone) {
      drawGemAt(ctx, layout, ev.lane, headProgress, isDual, 0, lightweightGems, reducedMotion);
    }
  }
}

export { computeLayout };
